import BSSTaggingManager from '@main/externals/bss-package-tag-tool/js/bss-manager.js';
import logger from '@main/externals/bss-package-logger/js/logger';
import helper from '@main/externals/bss-package-helper/js/helper';
import { isEmpty } from 'lodash';
var _toolbox;
/**
 * @class Entity
 * @desc
 */
var Entity = /** @class */ (function () {
    /**
     * @desc Entity constructor - generates all necessary objects and initialize entity
     * @param {String} entitySelector - selector for the Entity
     * @param {String} entityId - Entity ID
     * @param {object} config - configuration
     * @param {Map} componentsModules - components modules map
     * @param {EventManager} eventManager - event manager object
     * @param {Formatter} formatter - formatter object
     * @param {Resolution} resolution - resolution object
     * @param {Object} toolbox - toolbox object
     * @param {Number} instanceId
     * @param {string} elementType - type of the element(entity): nc, us, sc, bc, oa, pc
     */
    function Entity(entitySelector, entityId, config, componentsModules, eventManager, formatter, resolution, toolbox, instanceId, elementType, isWidgetInWidget, loaderSelector, theme, isLocaleAphelia, standalone) {
        if (!instanceId) {
            instanceId = toolbox.$.now();
        }
        if (!standalone)
            helper.validateMethodParams(arguments, {
                entitySelector: 'string',
                entityId: 'string',
                config: 'object',
                componentsModules: 'Map',
                eventManger: 'EventManager',
                formatter: 'Formatter',
                resolution: 'Resolution',
                toolbox: 'object',
            });
        logger.info('Entity: ' + this._entityInstanceId + ' => constructor | Params: ', entitySelector, entityId, config, instanceId, componentsModules, eventManager, formatter, resolution, logger, toolbox, elementType);
        _toolbox = toolbox;
        /**
         * Type of the element(entity):
         * nc, us, sc, bc, oa, pc
         */
        this._elementType = elementType;
        this._standalone = standalone;
        /**
         * @type {String}
         * @private
         */
        this._loaderSelector = loaderSelector;
        /**
         * @type {Boolean}
         * @private
         */
        this._isWidgetInWidget = isWidgetInWidget;
        /**
         * @type {string}
         * @private
         */
        this._theme = theme;
        /**
         * @type {boolean}
         * @private
         */
        this._isLocaleAphelia = isLocaleAphelia;
        /**
         * @type {Formatter}
         * @private
         */
        this._formatter = formatter;
        /**
         * @type {Resolution}
         * @private
         */
        this._resolution = resolution;
        /**
         * @type {EventManager}
         * @private
         */
        this._eventManager = eventManager;
        /**
         * @type {Number}
         * @private
         */
        this._entityInstanceId = instanceId;
        /**
         * @type {String}
         * @private
         */
        this._entityId = entityId;
        /**
         * @type {String|Null}
         * @private
         */
        this._entityActiveProductId = null;
        /**
         * @desc contains map of all created components instances
         * @type {Array}
         * @private
         */
        this._components = []; //new Map();
        /**
         * @desc stores all loaded components types
         * @type {Map}
         * @private
         */
        this._componentsMap = componentsModules;
        /**
         * @desc stores Entity configuration object
         * @type {Object}
         * @private
         */
        this._entityConfiguration = config;
        /**
         * @type {String}
         * @desc entity selector
         * @private
         */
        this._entitySelector = entitySelector;
        /**
         * @desc stores Entity container object
         * @type {jQuery|HTMLElement}
         * @private
         */
        this._entityContainer = _toolbox.$(entitySelector);
        /**
         * @desc components version string
         * @type {String}
         */
        this.componentsVersion = '';
        /**
         * @type {BSSTaggingManager}
         * @private
         */
        this._taggingManager = null;
        /**
         * @desc critical error message
         * @type {String}
         * @private
         */
        this._criticalErrorMsg = '';
        /**
         * @desc flag set to true when entity is destroyed during some error
         * @type {Boolean}
         * @private
         */
        this._isEntityDestroyedOnError = false;
        /**
         * @desc additional config from amd file, e.g. for overlay
         * @type {Map}
         * @private
         */
        this._additionalOverlayConfig = {};
        /**
         * @desc overlay inside entity when additional config set with active external overlay
         * @type {jQuery|HTMLElement}
         * @private
         */
        this.$inactiveOverlayElement = '';
        /**
         * @desc active overlay
         * @type {jQuery|HTMLElement}
         * @private
         */
        this.$overlayElement = '';
        /**
         * @desc if element is injected into popup, otherwise it is injected into page body
         * @type {Boolean}
         */
        this._isInPopup = this._isElementInjectedInPopup();
        /**
         * @desc parameters that must be added to every recalculation
         * @type {Record<string, string | number | boolean> | null}
         */
        this._additionalParamsAddedToRecalculation = null;
        this._addOverlayElement();
        this._setTheme();
        this._subscribeEvents();
        this._createComponents();
        this._setTagging();
    }
    Object.defineProperty(Entity.prototype, "entityContainer", {
        /**
         * @desc Entity container getter
         * @return {jQuery|HTMLElement}
         */
        get: function () {
            return this._entityContainer;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Entity.prototype, "entityConfiguration", {
        /**
         * @desc Entity configuration object getter
         * @return {Object}
         */
        get: function () {
            return this._entityConfiguration;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Entity.prototype, "components", {
        /**
         * @desc component map getter
         * @return {Map}
         */
        get: function () {
            return this._components;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Entity.prototype, "entityInstanceId", {
        /**
         * @desc entityInstance ID getter
         * @return {Number}
         */
        get: function () {
            return this._entityInstanceId;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Entity.prototype, "entityActiveProductId", {
        /**
         * @desc active product ID getter
         * @return {String|Null} product ID or null
         */
        get: function () {
            return this._entityActiveProductId;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * @desc Check if element is injected into popup and add 'location' class for element styling.
     * There are 2 injection types available for the element:
     * - location: "body"
     * - location: "popup"
     * "location" should be set by the client into initial configuration JSON, otherwise
     * the old mechanism will determine if element is in popup or not ("bss-appended-to-popup" class).
     * @return { boolean }
     * @private
     */
    Entity.prototype._isElementInjectedInPopup = function () {
        // only when location exists in initial configuration
        var location = _toolbox.location;
        if (location)
            return location === 'popup';
        var isInModalPopup = this._entityContainer.parents('.modal-body').length;
        if (isInModalPopup)
            return true;
        var isPopupClass = this._entityContainer
            .closest('.bss-amd')
            .hasClass('bss-appended-to-popup');
        return !!isPopupClass;
    };
    /**
     * @desc method subscribes Entity on basic events
     * @listens {bss-component-changed-$ENTITY-INSTANCE-ID}
     * @listens {bss-data-received-$ENTITY-INSTANCE-ID}
     * @listens {bss-text-received-$ENTITY-INSTANCE-ID}
     * @listens {bss-show-entity-overlay-$ENTITY-INSTANCE-ID}
     * @listens {bss-hide-entity-overlay-$ENTITY-INSTANCE-ID}
     * @listens {bss-entity-destroy-$ENTITY-INSTANCE-ID}
     * @listens {bss-entity-prod-change-$ENTITY-INSTANCE-ID}
     * @listens {bss-step-navigation-OA-rightbox-button-mode-$ENTITY-INSTANCE-ID}
     * @listens {bss-recalculation-add-params-$ENTITY-INSTANCE-ID}
     * @listens {bss-additional-config-applied} public event
     * @private
     */
    Entity.prototype._subscribeEvents = function () {
        logger.info('Entity: ' + this._entityInstanceId + ' => function : _subscribeEvents');
        this._eventManager.subscribe('bss-component-changed-' + this._entityInstanceId, this._onComponentChange.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe('bss-data-received-' + this._entityInstanceId, this._onDataReceived.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe('bss-text-received-' + this._entityInstanceId, this._onTextReceived.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe('bss-show-entity-overlay-' + this._entityInstanceId, this._onShowOverlay.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe('bss-hide-entity-overlay-' + this._entityInstanceId, this._onHideOverlay.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe('bss-entity-destroy-' + this._entityInstanceId, this._destroyEntityOnError.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe('bss-entity-prod-change-' + this._entityInstanceId, this._setEntityActiveProdId.bind(this), 'Entity-' + this._entityInstanceId);
        this._eventManager.subscribe("bss-recalculation-add-params-".concat(this._entityInstanceId), this._onAdditionalParamsAddedToRecalculation.bind(this), "Entity-".concat(this._entityInstanceId));
        document.addEventListener('bss-additional-config-applied', this._applyAdditionalConfig.bind(this));
        this._eventManager.subscribe('bss-step-navigation-OA-rightbox-button-mode', this._positionOverlay.bind(this), 'Entity-' + this._entityInstanceId);
    };
    /**
     * @desc set additional parameters to be added to every recalculation
     * @param {String} publisherName publisher name
     * @param {Object} params params to add
     * @private
     */
    Entity.prototype._onAdditionalParamsAddedToRecalculation = function (publisherName, params) {
        this._additionalParamsAddedToRecalculation = params;
    };
    /**
     * @desc method initialize tagging
     * @private
     */
    Entity.prototype._setTagging = function () {
        logger.info('Entity: ' + this._entityInstanceId + ' => function : _setTagging');
        var tagging = {};
        var taggingConfig = {
            resultData: this._entityConfiguration.resultData,
            texts: this._entityConfiguration.layout.texts,
        };
        var driver;
        if (_toolbox._.has(this._entityConfiguration.layout, 'behaviour') &&
            _toolbox._.has(this._entityConfiguration.layout.behaviour, 'tagging')) {
            tagging = this._entityConfiguration.layout.behaviour.tagging;
        }
        taggingConfig.tagging = tagging;
        driver = tagging.driverName ? tagging.driverName : '';
        if (!isEmpty(tagging)) {
            this._taggingManager = new BSSTaggingManager(taggingConfig, _toolbox, this._eventManager, driver);
        }
        this.initDXPTagging();
    };
    /**
     * Initialize DXP Tagging for all components, DXP tagging is stored in toolbox and will be available after initialization to all.
     */
    Entity.prototype.initDXPTagging = function () {
        var _a;
        if (!((_a = this._entityConfiguration) === null || _a === void 0 ? void 0 : _a.layout.dxpTagging)) {
            return;
        }
        if (!_toolbox.dxpTagging) {
            _toolbox.dxpTagging = {};
        }
        _toolbox.dxpTagging[this._entityId] =
            this._entityConfiguration.layout.dxpTagging(_toolbox._.cloneDeep({
                currentProductId: _toolbox.currentProductId,
                entityId: this._entityId,
                entityInstanceId: this._entityInstanceId,
                elementType: this._elementType,
                layoutTexts: _toolbox.layoutTexts,
                resultData: this._entityConfiguration.resultData,
            }));
    };
    /**
     * @desc set theme class to entity container
     * @private
     */
    Entity.prototype._setTheme = function () {
        logger.info('Entity: ' + this._entityInstanceId + ' => function : _setTheme');
        if (typeof this._entityConfiguration.layout !== 'undefined' ||
            typeof this._entityConfiguration.layout.theme !== 'undefined') {
            this._entityContainer.addClass(this._entityConfiguration.layout.theme);
        }
        this._setElementClass();
        /**
         * The sequence in the array is important.
         * 'tor' modals refer to classNameContent as '.tor-modal-content' or '#or-dialog-modal'
         * The two above must be in this sequence - '#or-dialog-modal' will be set if a client has such class in a modal, otherwise '.tor-modal-content'
         * '#or-dialog-modal' should be chosen on live pages to display infohints correctly
         */
        var modalDialog = [
            '.material-box',
            '.tor-modal-content',
            '#or-dialog-modal',
            '.modal-dialog',
        ];
        var modalHeader = ['.tor-modal-header', '.modal-header'];
        var modalBody = ['.material-box', '.tor-modal-body', '.modal-body'];
        var classNameContent;
        var classNameHeader;
        var classNameBody;
        var bodySpaceLeft;
        if (this._isInPopup) {
            var self_1 = this;
            modalDialog.forEach(function (el) {
                if (self_1._entityContainer.closest(el.toString()).length) {
                    classNameContent = el;
                }
            });
            if (classNameContent) {
                modalHeader.forEach(function (el) {
                    if (self_1._entityContainer.closest(classNameContent).find(el.toString())
                        .length) {
                        classNameHeader = el;
                    }
                });
            }
            modalBody.forEach(function (el) {
                if (self_1._entityContainer.closest(el.toString()).length) {
                    classNameBody = el;
                }
            });
            this._entityContainer.attr({
                'data-name-content': classNameContent,
                'data-name-header': classNameHeader,
                'data-name-body': classNameBody,
            });
            this._entityContainer
                .closest('.bss-entity-container')
                .addClass('bss-entity-content-adjust');
            this._entityContainer.attr({ 'is-popup': this._isInPopup });
        }
    };
    /**
     * Set element class, eg bss-nc-element
     * @private
     */
    Entity.prototype._setElementClass = function () {
        logger.info('Entity: ' + this._entityInstanceId + ' => function : _setElementClass');
        this._entityContainer.addClass("bss-element bss-".concat(this._elementType, "-element"));
        if (this._theme)
            this._entityContainer.addClass("bss-theme-".concat(this._theme));
    };
    /**
     * @desc method fires create method for each component
     * @emits {bss-entity-created-$ENTITY-INSTANCE-ID}
     */
    Entity.prototype._createComponents = function () {
        var _this = this;
        logger.info('Entity: ' + this._entityInstanceId + ' => function : _createComponents');
        var componentsByLevel = _toolbox._.groupBy(this._entityConfiguration.layout.components, 'level');
        var notCreatedComponentsConfig = [];
        var isProductIdExist = true;
        _toolbox._.forEach(componentsByLevel, function (val) {
            var _a;
            var componentsByOrder = _toolbox._.sortBy(val, function (elm) {
                var _a;
                return (_a = elm === null || elm === void 0 ? void 0 : elm.order) !== null && _a !== void 0 ? _a : 0;
            });
            for (var i = 0, length = componentsByOrder.length; i < length; i++) {
                if (componentsByOrder[i] === undefined)
                    return;
                if (((_a = _this._entityConfiguration) === null || _a === void 0 ? void 0 : _a.resultData) !== undefined) {
                    var productArray = Object.keys(_this._entityConfiguration.resultData);
                    isProductIdExist = productArray.some(function (productId) {
                        return productId === componentsByOrder[i].productId[0];
                    });
                }
                if (isProductIdExist ||
                    componentsByOrder[i].productId[0] === 'general') {
                    if (!_this._createComponent(componentsByOrder[i])) {
                        notCreatedComponentsConfig.push(componentsByOrder[i]);
                    }
                }
            }
        });
        // check if there is any correct component to render in entity
        if (this._analizeComponentsErrors(notCreatedComponentsConfig)) {
            this._entityContainer.addClass('is-initialized');
            this._eventManager.publish('bss-entity-created-' + this._entityInstanceId, 'Entity-' + this._entityInstanceId);
        }
        else {
            this._destroyEntityOnError();
        }
    };
    /**
     * @desc Check if there are any errors after components initialization
     * @param {Array} notCreatedComponentsConfig - array of not created components
     * @return {Boolean} return true if all critical components are created; return false if there is no components or if critical component has error
     */
    Entity.prototype._analizeComponentsErrors = function (notCreatedComponentsConfig) {
        var _this = this;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _analizeComponentsErrors' +
            ' | Params: ', notCreatedComponentsConfig);
        var isAbleToInitEntity = true;
        var createdCompNum = 0;
        // if there is NO created components, show error template
        for (var i = 0, len = this._components.length; i < len; i++) {
            this._components[i].forEach(function () {
                createdCompNum++;
            });
        }
        if (!createdCompNum) {
            this._criticalErrorMsg = 'Error: No component was created.';
            logger.error('Entity => _analizeComponentsErrors: | Error: No component was created.');
            isAbleToInitEntity = false;
            return isAbleToInitEntity;
        }
        // check if any of NOT created components is critical or have critical error
        if (notCreatedComponentsConfig.length) {
            logger.error('Entity => _analizeComponentsErrors: | Error: Some components were not created.', notCreatedComponentsConfig);
            var self_2 = this;
            _toolbox._.forEach(notCreatedComponentsConfig, function (compConfig) {
                if (!self_2._checkComponentDependencies(compConfig)) {
                    isAbleToInitEntity = false;
                }
            });
        }
        // if no errors, check if any of created components have errorObject
        if (isAbleToInitEntity) {
            for (var i = 0, len = this._components.length; i < len; i++) {
                if (!this._isEntityDestroyedOnError) {
                    // stop loop when entity was destroyed due to critical error on some component
                    this._components[i].forEach(function (value, key, map) {
                        var compErrorObj = value.errorObject;
                        if (!isEmpty(compErrorObj) &&
                            compErrorObj.errorSeverity === 'critical') {
                            console.log(value, 'error');
                            if (!_this._checkComponentDependencies(value._config)) {
                                isAbleToInitEntity = false;
                            }
                        }
                    });
                }
            }
        }
        return isAbleToInitEntity;
    };
    /**
     * @desc check component significance and its dependence on other components
     * @param {Object} compConfig - component configuration
     * @return {Boolean} return false if Entity cannot be created due to component error
     */
    Entity.prototype._checkComponentDependencies = function (compConfig) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _checkComponentDependencies' +
            ' | Params: ', compConfig);
        if (compConfig.significance === 'critical' && !(compConfig === null || compConfig === void 0 ? void 0 : compConfig.optional)) {
            // if component is main and critical - show error page
            if (compConfig.isParentComp) {
                this._criticalErrorMsg =
                    'Error: Main (parent) component ' +
                        compConfig.componentType +
                        ' was not created.';
                logger.error('Entity => _checkComponentDependencies: | Error: Main (parent) component ' +
                    compConfig.componentType +
                    ' was not created.', compConfig);
                return false;
            }
            // if component is a dependent child - switch off all components that are related to it's parent ID and invalid product
            else {
                var prodIds = _toolbox._.without(compConfig.productId, 'general');
                this._switchOffComponentsWithInvalidProduct(compConfig.parentId, prodIds);
                return true;
            }
        }
        return true;
    };
    /**
     * @desc switch off all components dependent on invalid products
     * @param {String} parentId - parent component ID
     * @param {Array} prodIds - array with invalid products IDs
     */
    Entity.prototype._switchOffComponentsWithInvalidProduct = function (parentId, prodIds) {
        var _this = this;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _switchOffComponentsWithInvalidProduct' +
            ' | Params: ', parentId, prodIds);
        var allComponents = this._entityConfiguration.layout.components;
        var componentsByParentId = _toolbox._.groupBy(allComponents, 'parentId');
        var parentCompConfig = _toolbox._.find(allComponents, function (o) {
            return o.componentId === parentId;
        });
        // find all children dependent on parent with given ID
        _toolbox._.forEach(componentsByParentId, function (compArr, key) {
            if (key === parentId) {
                _toolbox._.forEach(compArr, function (comp) {
                    var compTempId = comp.componentType + '-' + comp.componentId;
                    var compProdIds = _toolbox._.without(comp.productId, 'general');
                    var isInvalidProd = false;
                    var compObj;
                    try {
                        compObj = _this._components[comp.level].get(compTempId);
                    }
                    catch (error) {
                        console.log(error);
                    }
                    // check if this child depends on invalid products
                    for (var i = 0, len = prodIds.length; i < len; i++) {
                        if (_toolbox._.indexOf(compProdIds, prodIds[i]) >= 0) {
                            isInvalidProd = true;
                        }
                    }
                    // switch this component and remove it from created components list
                    if (isInvalidProd && typeof compObj !== 'undefined') {
                        logger.error('Entity => _switchOffComponentsWithInvalidProduct: | Error: Switch off critical, child component ' +
                            comp.componentType +
                            '.', comp);
                        compObj.switchOffProduct(prodIds);
                        _this._components[comp.level].delete(comp.componentType + '-' + comp.componentId);
                    }
                });
            }
        });
        // find parent component with given ID and switch it off
        if (typeof parentCompConfig !== 'undefined') {
            var compTempId = parentCompConfig.componentType + '-' + parentId;
            var parentComponent = this._components[parentCompConfig.level].get(compTempId);
            if (typeof parentComponent !== 'undefined') {
                logger.error('Entity => _switchOffComponentsWithInvalidProduct: | Error: Switch off parent component ' +
                    parentCompConfig.componentType +
                    '.', parentCompConfig);
                parentComponent.switchOffProduct(prodIds);
            }
        }
    };
    /**
     * @desc method creates component object when container for component is available in DOM
     * @param {Object} componentConfig  - component configuration
     * @return {Boolean} - return true when component is created
     * @private
     */
    Entity.prototype._createComponent = function (componentConfig) {
        var _a;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _createComponent' +
            ' | Params: ', componentConfig);
        if (this._standalone) {
            var componentObj = void 0;
            try {
                var componentSelector = componentConfig.level === 0
                    ? null
                    : '#' + componentConfig.containerId;
                var component = this._componentsMap.get(componentConfig.componentType);
                componentObj = new component.module(this._entityContainer, componentSelector, componentConfig, this._eventManager, this._formatter, this._resolution, _toolbox); // eslint-disable-line new-cap
                component.version = componentObj.getVersion();
                // this._components[componentConfig.level].set(
                //   componentConfig.componentType + '-' + componentConfig.componentId,
                //   componentObj
                // );
                logger.info('Entity => _createComponent: ' +
                    componentConfig.componentType +
                    ' created');
                return true;
            }
            catch (err) {
                // destroy component if error occurs
                if (typeof componentObj !== 'undefined' &&
                    typeof componentObj.destroy === 'function') {
                    componentObj.destroy();
                }
                logger.error('Entity => _createComponent: | Error: Component ' +
                    componentConfig.componentType +
                    ' was not created - ', err, componentConfig);
                return false;
            }
        }
        var componentSelector = componentConfig.level === 0 ? null : '#' + componentConfig.containerId; //default level 0 is inside _entityTemplate
        var entityContainer = this._entityContainer;
        if (typeof this._components[componentConfig.level] === 'undefined') {
            this._components[componentConfig.level] = new Map();
        }
        componentConfig.resultData = {};
        componentConfig.texts = {};
        componentConfig.entityInstanceId = this._entityInstanceId;
        for (var i = 0, len = componentConfig.productId.length; i < len; i++) {
            if (componentConfig.productId[i] === 'resultData') {
                componentConfig.resultData = this._entityConfiguration.resultData;
            }
            else {
                componentConfig.resultData[componentConfig.productId[i]] =
                    this._entityConfiguration.resultData[componentConfig.productId[i]];
                componentConfig.texts[componentConfig.productId[i]] =
                    this._entityConfiguration.layout.texts[componentConfig.productId[i]];
            }
        }
        if (typeof componentConfig.outsideEntityContainer !== 'undefined' &&
            _toolbox.$(componentConfig.outsideEntityContainer).length &&
            entityContainer.parent().hasClass('bss-entity-onlineApproval')) {
            entityContainer = _toolbox.$(componentConfig.outsideEntityContainer);
            if (typeof componentConfig.outerContainerId !== 'undefined') {
                componentSelector = componentConfig.outerContainerId;
            }
        }
        if ((entityContainer.find(componentSelector).length && componentSelector) ||
            (entityContainer.length && !componentConfig.level)) {
            if (typeof this._componentsMap.get(componentConfig.componentType) ===
                'undefined') {
                logger.error('Entity => _createComponent: | Error: Component ' +
                    componentConfig.componentType +
                    ' is undefined.', componentConfig);
                return false;
            }
            var componentObj = void 0;
            try {
                var hideResultBox = (_a = _toolbox.resultDataFlat[componentConfig.productId[0]]) === null || _a === void 0 ? void 0 : _a.hideResultBox;
                if (hideResultBox &&
                    (hideResultBox === true || hideResultBox === 'true') &&
                    (componentConfig === null || componentConfig === void 0 ? void 0 : componentConfig.isResultBox) === true) {
                    return true;
                }
                var component = this._componentsMap.get(componentConfig.componentType);
                if (this._isLocaleAphelia)
                    componentConfig.isLocaleAphelia = this._isLocaleAphelia;
                componentObj = new component.module(entityContainer, componentSelector, componentConfig, this._eventManager, this._formatter, this._resolution, _toolbox); // eslint-disable-line new-cap
                component.version = componentObj.getVersion();
                this._components[componentConfig.level].set(componentConfig.componentType + '-' + componentConfig.componentId, componentObj);
                logger.info('Entity => _createComponent: ' +
                    componentConfig.componentType +
                    ' created');
                return true;
            }
            catch (err) {
                // destroy component if error occurs
                if (typeof componentObj !== 'undefined' &&
                    typeof componentObj.destroy === 'function') {
                    componentObj.destroy();
                }
                logger.error('Entity => _createComponent: | Error: Component ' +
                    componentConfig.componentType +
                    ' was not created - ', err, componentConfig);
                return false;
            }
        }
        else {
            logger.error('Entity => _createComponent: | Error: Component ' +
                componentConfig.componentType +
                ' container (' +
                componentSelector +
                ') is undefined.', componentConfig);
            return false;
        }
        return true;
    };
    /**
     * @desc function invoked when the component is changed
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher /productId.field: value/
     * @emits {bss-data-changed-$ENTITY-INSTANCE-ID}
     * @private
     */
    Entity.prototype._onComponentChange = function (publisherName, params) {
        var _this = this;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _onComponentChange' +
            ' | Params: ', publisherName, params);
        var publishParams = {};
        _toolbox._.forIn(params, function (value, key) {
            _this._entityConfiguration.resultData[key] = _toolbox._.defaults(value, _this._entityConfiguration.resultData[key]);
            publishParams[key] = _this._entityConfiguration.resultData[key];
        });
        if (this._additionalParamsAddedToRecalculation) {
            var productId = Object.keys(params)[0];
            var productPublishParams = productId ? publishParams[productId] : {};
            if (!isEmpty(productPublishParams)) {
                this._addAdditionalParamsToPublishParams(productPublishParams, this._additionalParamsAddedToRecalculation);
            }
        }
        this._eventManager.publish('bss-data-changed-' + this._entityInstanceId, 'Entity-' + this._entityInstanceId + '/' + publisherName, publishParams);
    };
    /**
     * @desc add additionala params to product publish params object
     * @param {Object} productPublishParams product publish params object
     * @param {Object} params params to be added
     * @private
     */
    Entity.prototype._addAdditionalParamsToPublishParams = function (productPublishParams, params) {
        for (var key in params) {
            if (Object.hasOwn(params, key)) {
                productPublishParams[key] = params[key];
            }
        }
    };
    /**
     * @desc function invoked when dataResult is received
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher
     * @emits {bss-entity-update-$ENTITY-INSTANCE-ID-$PRODUCT-ID}
     * @emits {bss-entity-critical-error-$ENTITY-INSTANCE-ID}
     * @private
     */
    Entity.prototype._onDataReceived = function (publisherName, params) {
        var _this = this;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _onDataReceived' +
            ' | Params: ', publisherName, params);
        var offerId = null;
        if (params.hasOwnProperty('offerId')) {
            offerId = params.offerId;
        }
        try {
            var resultData = {};
            var updateTimestamp_1 = params.updateTimestamp;
            //this param is removed because we iterates params keys and the assumption was to iterate keys that are real products ids
            delete params.updateTimestamp;
            if (params.resultData) {
                resultData = params.resultData;
            }
            else if (params) {
                resultData = params;
            }
            // publish step validation error flag if it's occured
            var stepValidationError = params.error_code === 'VALIDATOR_ERROR';
            this._eventManager.publish('bss-step-validation-error-' + this._entityInstanceId, 'Entity-' + this._entityInstanceId, { stepValidationError: stepValidationError });
            _toolbox._.forIn(resultData, function (value, key) {
                var partial = {
                    resultData: {},
                    updateTimestamp: updateTimestamp_1,
                };
                partial.resultData[key] = value;
                //set new product configuration
                _this._entityConfiguration.resultData[key] = value;
                _this._eventManager.publish('bss-entity-update-' + _this._entityInstanceId + '-' + key, 'Entity-' + _this._entityInstanceId, partial);
            });
        }
        catch (e) {
            this._criticalErrorMsg = 'Error on data received: ' + e + '.';
            var err = {
                errorMsg: this._criticalErrorMsg,
                entityInstanceId: this._entityInstanceId,
                offerId: offerId,
            };
            this._eventManager.publish('bss-entity-critical-error-' + this._entityInstanceId, 'bss-entity', err);
        }
    };
    /**
     * @desc function invoked when text is received
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher
     * @emits {bss-entity-update-$ENTITY-INSTANCE-ID-$PRODUCT-ID}
     * @emits {bss-entity-critical-error-$ENTITY-INSTANCE-ID}
     * @private
     */
    Entity.prototype._onTextReceived = function (publisherName, params) {
        var _this = this;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _onTextReceived' +
            ' | Params: ', publisherName, params);
        try {
            _toolbox._.forIn(params.layout.texts, function (value, key) {
                var partial = {
                    texts: {},
                    resultData: {},
                    updateTimestamp: params.updateTimestamp,
                };
                partial.texts[key] = value;
                partial.resultData[key] = _this._entityConfiguration.resultData[key];
                _this._eventManager.publish('bss-entity-update-' + _this._entityInstanceId + '-' + key, 'Entity-' + _this._entityInstanceId, partial);
            });
        }
        catch (e) {
            this._criticalErrorMsg = 'Error on text received: ' + e + '.';
            var err = {
                errorMsg: this._criticalErrorMsg,
                entityInstanceId: this._entityInstanceId,
            };
            this._eventManager.publish('bss-entity-critical-error-' + this._entityInstanceId, 'bss-entity', err);
        }
    };
    /**
     * @desc compare data
     * @param {Object} collection - data to compare and update this._entityConfiguration object to income
     * @param {Object} inputData - income data
     * @return {Object} - object with changed fields
     * @private
     */
    Entity.prototype._compareData = function (collection, inputData) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _compareData' +
            ' | Params: ', collection, inputData);
        var partial = {};
        _toolbox._.forEach(inputData, function (value, key) {
            if (typeof collection[key] !== 'undefined' && collection[key] !== value) {
                partial[key] = value;
                collection[key] = value;
            }
        });
        return partial;
    };
    /**
     * @desc destroy Entity and all components
     * @public
     */
    Entity.prototype.destroyEntity = function () {
        logger.info('Entity: ' + this._entityInstanceId + ' => function : destroyEntity');
        this._eventManager.unsubscribeByOwner('Entity-' + this._entityInstanceId);
        for (var i = this._components.length - 1, len = 0; i >= len; i--) {
            this._components[i].forEach(function (value, key, map) {
                value.destroy();
                map.delete(key);
            });
        }
        this._components = [];
        this._removeOverlayElement();
        if (this._taggingManager) {
            this._taggingManager.destroy();
        }
        logger.info('Entity: ' + this._entityInstanceId + ' IS DESTROYED');
    };
    /**
     * @desc destroy Entity (and all components) when some error occurs
     * @param {String} [publisherName=null] - publisher name
     * @param {Object} [param=null] - optional error object received from component that emits 'bss-entity-destroy' event
     * @emits {bss-entity-critical-error-$ENTITY-INSTANCE-ID}
     */
    Entity.prototype._destroyEntityOnError = function (publisherName, param) {
        if (publisherName === void 0) { publisherName = null; }
        if (param === void 0) { param = null; }
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _destroyEntityOnError' +
            ' | Params: ', publisherName, param);
        this._isEntityDestroyedOnError = true;
        var entityErr = { errorMsg: '' }; // do NOT define 'entityInstanceId' - we need undefined value to show error template (not revert entity state)
        if (param !== null && typeof param.errorMsg !== 'undefined') {
            entityErr.errorMsg = param.errorMsg;
        }
        else {
            entityErr.errorMsg = this._criticalErrorMsg;
        }
        this._eventManager.publish('bss-entity-critical-error-' + this._entityInstanceId, 'bss-entity', entityErr);
        this.destroyEntity();
    };
    /**
     * @desc add entity overlay element
     * @private
     */
    Entity.prototype._addOverlayElement = function () {
        logger.info('Entity: ' + this._entityInstanceId + ' => function : _addOverlayElement');
        if (this._isWidgetInWidget) {
            this.$overlayElement = _toolbox.$('#bssEntityOverlay');
            if (this.$overlayElement && this.$overlayElement.length) {
                return false;
            }
            else {
                var loaderContainer = _toolbox.$(this._loaderSelector);
                this._addOverlay(loaderContainer);
            }
        }
        if (this.$overlayElement && this.$overlayElement.length) {
            // bug out if the overlay's already there
            return false;
        }
        this._addOverlay(this._entityContainer);
    };
    /**
     * @desc adds overlay to DOM
     * @private
     */
    Entity.prototype._addOverlay = function (entityContainer) {
        this.$overlayElement = _toolbox.$("<div id=\"bssEntityOverlay\" class=\"bss-entity-overlay\">\n          <div class=\"bss-fixed-pointer\">\n              <span class=\"bss-entity-loader\"></span>\n          <div>\n        </div>");
        entityContainer.append(this.$overlayElement);
    };
    /**
     * @desc remove entity overlay element
     * @private
     */
    Entity.prototype._removeOverlayElement = function () {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _removeOverlayElement');
        if (this.$overlayElement) {
            this.$overlayElement.remove();
        }
    };
    /**
     * @desc show / hide entity overlay
     * @param {Boolean} show
     * @private
     */
    Entity.prototype._toggleOverlay = function (show) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _toggleOverlay' +
            ' | Params: ', show);
        if (!show)
            this.$overlayElement.removeClass('is-final-submit');
        var topPos = 0;
        var isAdditionalOverlayConfig = Object.keys(this._additionalOverlayConfig).length;
        var isShowNavigation = isAdditionalOverlayConfig
            ? this._additionalOverlayConfig.showNavigation
            : false;
        var tabsDropdownStepNavigation$ = _toolbox.$('.bss-step-navigation_header-navigation');
        var navElTriggerW = tabsDropdownStepNavigation$.length
            ? tabsDropdownStepNavigation$
            : _toolbox.$('.bss-step-navigation_trigger-wrapper');
        var navHeader = _toolbox.$('.bss-step-navigation_header');
        if (navElTriggerW.length && isAdditionalOverlayConfig) {
            // additional config with external overlay
            if (this._isInPopup) {
                if (navHeader.hasClass('is-fixed') && isShowNavigation) {
                    topPos = navHeader.outerHeight(true);
                }
                else if (isShowNavigation) {
                    topPos =
                        navHeader.outerHeight(true) +
                            parseInt(_toolbox.$('.modal-body').css('padding-top')) +
                            _toolbox.$('.modal-header').outerHeight(true);
                }
                else {
                    //navigation hidden on modal
                    topPos = 0;
                }
            }
            else {
                // desktop mode
                if (isShowNavigation) {
                    topPos = navElTriggerW.offset().top + navElTriggerW.outerHeight();
                }
                else {
                    // navigation hidden on desktop
                    topPos = 0;
                }
            }
        }
        if (this.$overlayElement) {
            this.$overlayElement.toggleClass('is-active', show);
            if (isAdditionalOverlayConfig) {
                this.$overlayElement.css({ top: topPos + 'px' });
            }
            else {
                this.headerHeight = 0;
                var visibilityElRect = this._entityContainer[0].getBoundingClientRect();
                var windowHeight = _toolbox.$(window.top).height();
                var visibleElementTopPosition = visibilityElRect.top < 0 ? 0 : visibilityElRect.top;
                var visibleElementBottomPosition = visibilityElRect.bottom > windowHeight
                    ? windowHeight
                    : visibilityElRect.bottom;
                var minimumOverlayHeight = 80;
                var visibleElementHeight = this._isWidgetInWidget
                    ? visibilityElRect.height
                    : visibleElementBottomPosition - visibleElementTopPosition;
                this.$overlayElement.css({
                    height: visibleElementHeight > minimumOverlayHeight
                        ? visibleElementHeight + 'px'
                        : minimumOverlayHeight + 'px',
                });
                this.$overlayElement.css({
                    top: !this._isInPopup
                        ? // Cover only element content from its top
                            // we cant cover client content
                            visibleElementTopPosition + 'px'
                        : this.headerHeight + 'px',
                });
            }
            if (navElTriggerW.length &&
                isAdditionalOverlayConfig &&
                !this._isInPopup &&
                this.$overlayElement.hasClass('not-scrolled-to-top')) {
                //position overlay content so that it is visible in viewport if click not in right button (submit/next etc.)
                this.$overlayElement
                    .find('.bss-outer-entity-overlay-inner-wrapper')
                    .css({
                    top: _toolbox.$(window).scrollTop() -
                        this.$overlayElement.offset().top >
                        0
                        ? _toolbox.$(window).scrollTop() -
                            this.$overlayElement.offset().top +
                            'px'
                        : 0,
                });
            }
        }
    };
    /**
     * @desc on submit in last step add additional class to overlay
     * @desc if additional config set add class to overlay for positioning overlay content
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher
     * @private
     */
    Entity.prototype._positionOverlay = function (publisherName, params) {
        var _this = this;
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _positionOverlay' +
            ' | Params: ', publisherName, params);
        if (this.$overlayElement.length &&
            Object.keys(this._additionalOverlayConfig).length) {
            this.$overlayElement.removeClass('not-scrolled-to-top');
        }
        _toolbox._.forIn(params, function (value, key) {
            if (value.submit || (key == 'submit' && value === true)) {
                _this.$overlayElement.addClass('is-final-submit');
            }
        });
    };
    /**
     * @desc set additional config properties for e.g. overlay
     * @param {Object} e - object send by publisher
     * @private
     */
    Entity.prototype._applyAdditionalConfig = function (e) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _applyAdditionalConfig' +
            ' | Params: ', e);
        var externalOverlayConfig = e.detail.overlay;
        if (Object.keys(e).length) {
            if (externalOverlayConfig.externalOnDesktopSelector !== null) {
                this.$inactiveOverlayElement = this.$overlayElement;
                this.$overlayElement = _toolbox.$(externalOverlayConfig.externalOnDesktopSelector);
            }
            if (externalOverlayConfig.showNavigation) {
                this.$overlayElement.addClass('bss-overlay-with-navigation');
            }
        }
        this._additionalOverlayConfig = externalOverlayConfig;
    };
    /**
     * @desc handler for bss-show-entity-overlay event
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher
     * @private
     */
    Entity.prototype._onShowOverlay = function (publisherName, params) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _onShowOverlay' +
            ' | Params: ', publisherName, params);
        if (Object.keys(this._additionalOverlayConfig).length &&
            this.$inactiveOverlayElement.length) {
            this.$overlayElement
                .find('.bss-outer-entity-overlay-inner-wrapper')
                .html(this.$inactiveOverlayElement.html());
        }
        this._toggleOverlay(true);
    };
    /**
     * @desc handler for bss-hide-entity-overlay event
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher
     * @private
     */
    Entity.prototype._onHideOverlay = function (publisherName, params) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _onHideOverlay' +
            ' | Params: ', publisherName, params);
        this._toggleOverlay(false);
        if (this.$overlayElement.length &&
            Object.keys(this._additionalOverlayConfig).length) {
            this.$overlayElement.addClass('not-scrolled-to-top');
        }
    };
    /**
     * @desc set entity active product id when is set and changed
     * @param {String} publisherName - publisher name
     * @param {Object} params - params send by publisher
     * @private
     */
    Entity.prototype._setEntityActiveProdId = function (publisherName, params) {
        logger.info('Entity: ' +
            this._entityInstanceId +
            ' => function : _setEntityActiveProdId' +
            ' | Params: ', publisherName, params);
        this._entityActiveProductId = params.prodID;
    };
    return Entity;
}());
export default Entity;
